import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { apiRequest } from '../config/api';
import { Mail, User, Lock, UserIcon, ArrowRight, Eye, EyeOff, Check, Building2, UserPlus } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { loginWithToken, isAuthenticated } = useAuth();
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [requiresRegistration, setRequiresRegistration] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [registerForm, setRegisterForm] = useState({
    username: '',
    password: '',
    full_name: ''
  });
  const [registerLoading, setRegisterLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    const acceptInvite = async () => {
      try {
        const response = await apiRequest('/organizations/accept-invite', {
          method: 'POST',
          body: JSON.stringify({
            token: token
          })
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.requires_registration) {
            setRequiresRegistration(true);
            setInviteData(data);
          } else {
            setSuccess(true);
            // If user is logged in, redirect to home
            setTimeout(() => navigate('/'), 2000);
          }
        } else {
          const errorData = await response.json();
          setError(errorData.detail || 'Failed to accept invitation');
        }
      } catch (err) {
        setError('Network error. Please try again.');
        console.error('Accept invite error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      acceptInvite();
    }
  }, [token, navigate]);

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterLoading(true);

    try {
      const response = await apiRequest('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          email: inviteData.email,
          username: registerForm.username,
          password: registerForm.password,
          full_name: registerForm.full_name,
          invite_token: token
        })
      });

      if (response.ok) {
        const data = await response.json();
        loginWithToken(data); // This will set the user as authenticated
        setSuccess(true);
        setTimeout(() => navigate('/'), 2000);
      } else {
        const errorData = await response.json();
        showError(errorData.detail || 'Registration failed');
      }
    } catch (err) {
      showError('Network error. Please try again.');
      console.error('Registration error:', err);
    } finally {
      setRegisterLoading(false);
    }
  };

  // Password strength indicator
  const getPasswordStrength = () => {
    if (registerForm.password.length < 6) return 0;
    if (registerForm.password.length < 8) return 1;
    if (registerForm.password.match(/(?=.*[a-z])(?=.*[A-Z])/)) return 2;
    if (registerForm.password.match(/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/)) return 3;
    return 3;
  };

  const passwordStrength = getPasswordStrength();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-brand-600 rounded-2xl flex items-center justify-center shadow-lg mb-6 mx-auto">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
          <p className="text-gray-600 font-medium">Accepting invitation...</p>
        </div>
      </div>
    );
  }

  if (requiresRegistration && inviteData) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          {/* Logo and Brand */}
          <div className="text-center mb-8">
            <BrandLogo 
              size="md"
              title="Join the team"
              showText={true}
            />
            <div className="flex items-center justify-center mb-4">
              <div className="flex items-center bg-brand-50 text-brand-700 px-3 py-1 rounded-full text-sm font-medium">
                <Building2 className="h-4 w-4 mr-2" />
                {inviteData.organization_name}
              </div>
            </div>
            <p className="text-gray-600 text-sm">
              You've been invited to join <strong>{inviteData.organization_name}</strong>.<br />
              Create your account to accept the invitation.
            </p>
          </div>

          {/* Register Form Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <form onSubmit={handleRegister} className="space-y-6">
              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    value={inviteData.email}
                    disabled
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
              </div>

              {/* Username Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={registerForm.username}
                    onChange={(e) => setRegisterForm({ ...registerForm, username: e.target.value })}
                    required
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="Choose a username"
                  />
                </div>
              </div>

              {/* Full Name Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-gray-400">(optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <UserIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={registerForm.full_name}
                    onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })}
                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="Your full name"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    required
                    className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 transition-colors"
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                
                {/* Password Strength Indicator */}
                {registerForm.password && (
                  <div className="mt-2">
                    <div className="flex space-x-1 mb-1">
                      {[0, 1, 2, 3].map((level) => (
                        <div
                          key={level}
                          className={`h-1 flex-1 rounded-full ${
                            level <= passwordStrength
                              ? level < 2 ? 'bg-red-400' : level < 3 ? 'bg-yellow-400' : 'bg-brand-400'
                              : 'bg-gray-200'
                          }`}
                        />
                      ))}
                    </div>
                    <div className="text-xs text-gray-600 flex items-center">
                      {registerForm.password.length >= 6 && (
                        <>
                          <Check className="h-3 w-3 text-brand-500 mr-1" />
                          <span className="text-brand-600">At least 6 characters</span>
                        </>
                      )}
                      {registerForm.password.length < 6 && (
                        <span>Password must be at least 6 characters</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={registerLoading}
                className="group relative w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {registerLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Account...
                  </div>
                ) : (
                  <div className="flex items-center">
                    <UserPlus className="mr-2 h-4 w-4" />
                    Join Organization
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <div className="text-red-600 font-bold text-xl">!</div>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Invitation Error
              </h2>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="inline-flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors"
              >
                Go to Login
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-6 h-6 text-brand-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Welcome to the team!
              </h2>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                You have successfully joined the organization.<br />
                You can now start collaborating with your team.
              </p>
              {isAuthenticated ? (
                <button
                  onClick={() => navigate('/')}
                  className="inline-flex items-center px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors font-medium"
                >
                  Go to Dashboard
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={() => navigate('/login')}
                  className="inline-flex items-center px-6 py-3 bg-brand-600 text-white rounded-lg hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 transition-colors font-medium"
                >
                  Login to Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default AcceptInvite;
